import { Box, Typography } from "@mui/material";
import React, { useState } from "react";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const PARTENAIRES = [
  {
    name: "Jimenez Transport",
    type: "Transport & logistique",
    ville: "Région toulousaine",
  },
  {
    name: "Delample VRD",
    type: "Travaux publics",
    ville: "Région toulousaine",
  },
  { name: "BNi Toulouse", type: "Réseau d'affaires", ville: "Toulouse" },
];

const BENEFITS = [
  {
    icon: "🏢",
    title: "Je viens sur place",
    desc: "Pas de déplacement pour vos salariés. J'interviens directement dans vos locaux, sans perturber votre organisation.",
  },
  {
    icon: "⏱️",
    title: "30 minutes par séance",
    desc: "Des créneaux courts et efficaces. 6 salariés par demi-journée d'intervention, planifiés à l'avance.",
  },
  {
    icon: "📉",
    title: "Moins d'arrêts maladie",
    desc: "En traitant les douleurs musculaires et articulaires en amont, vous réduisez les troubles musculo-squelettiques (TMS), premières causes d'arrêts de travail.",
  },
  {
    icon: "😌",
    title: "Moins de stress",
    desc: "L'ostéopathie agit aussi sur le système nerveux végétatif. Les équipes reviennent détendues et plus productives.",
  },
  {
    icon: "💶",
    title: "Tarif dégressif",
    desc: "Plus votre équipe est grande, moins le coût est élevé par personne. Un tarif transparent, sans abonnement.",
  },
  {
    icon: "📅",
    title: "Suivi régulier possible",
    desc: "J'interviens depuis 1 an à raison d'une séance mensuelle chez certains partenaires. Un rythme qui s'adapte à vos besoins.",
  },
];

const inputSx = {
  width: "100%",
  background: "rgba(255,255,255,.08)",
  border: "1px solid rgba(255,255,255,.18)",
  borderRadius: "8px",
  padding: ".75rem 1rem",
  fontSize: ".88rem",
  color: "#fff",
  outline: "none",
  transition: `border-color ${trans}`,
  fontFamily: "inherit",
  "&::placeholder": { color: "rgba(255,255,255,.35)" },
  "&:focus": { borderColor: "rgba(255,255,255,.5)" },
};

const Partenaires = () => {
  const [form, setForm] = useState({
    nom: "",
    entreprise: "",
    email: "",
    telephone: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Partenariat entreprise  ${form.entreprise}`,
    );
    const body = encodeURIComponent(
      `Nom : ${form.nom}\nEntreprise : ${form.entreprise}\nTéléphone : ${form.telephone}\nEmail : ${form.email}\n\nMessage :\n${form.message}`,
    );
    window.open(
      `mailto:nicolaieffmaxime@gmail.com?subject=${subject}&body=${body}`,
    );
    setSent(true);
  };

  return (
    <>
      <SEO
        title="Ostéopathie en entreprise  Partenariats  Balma"
        description="Maxime Nicolaieff intervient dans vos locaux pour des séances ostéopathiques de 30 min. Réduction des douleurs au travail, mieux-être des équipes. Contactez-nous."
        path="/partenariats"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Partenariats entreprises",
              item: `${SITE_URL}/partenariats`,
            },
          ],
        }}
      />

      {/* ── Hero ──────────────────────────────── */}
      <Box component="section" sx={SX.pageHeroSection}>
        <Box sx={SX.pageHeroInner}>
          <Box component="span" sx={SX.sectionLabel}>
            Partenariats entreprises &amp; établissements
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Le bien-être de vos équipes,
            <br />
            <em>directement sur place.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            Je me déplace dans vos locaux pour proposer des consultations
            ostéopathiques à vos collaborateurs. Un levier concret pour réduire
            les douleurs au travail, les arrêts maladie et le stress des
            équipes.
          </Typography>
        </Box>
      </Box>

      {/* ── Bénéfices ─────────────────────────── */}
      <Box
        component="section"
        sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
      >
        <Box
          sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
        >
          <Box component="span" sx={SX.sectionLabel}>
            Pourquoi
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "2.5rem" }}
          >
            Ce que ça apporte <em>concrètement.</em>
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(3, 1fr)",
              },
              gap: "1.25rem",
            }}
          >
            {BENEFITS.map((b) => (
              <Box
                key={b.title}
                sx={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: R.lg,
                  padding: "1.5rem",
                  transition: `box-shadow ${trans}`,
                  "&:hover": { boxShadow: S.md },
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "1.5rem", display: "block", mb: ".75rem" }}
                >
                  {b.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: ".9rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".4rem",
                  }}
                >
                  {b.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".8rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {b.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Comment ça marche ─────────────────── */}
      <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
        <Box
          sx={{
            maxWidth: maxW,
            mx: "auto",
            px: { xs: "1rem", md: "2.5rem" },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: "3rem", md: "5rem" },
            alignItems: "center",
          }}
        >
          <Box>
            <Box component="span" sx={SX.sectionLabel}>
              Déroulement
            </Box>
            <Typography
              variant="h2"
              component="h2"
              sx={{ ...SX.displayTitle, mb: "1.25rem" }}
            >
              Comment se passe <em>une intervention ?</em>
            </Typography>
            <Typography
              sx={{
                fontSize: ".88rem",
                color: C.textMuted,
                lineHeight: 1.8,
                mb: "1.5rem",
              }}
            >
              Je viens directement dans vos locaux avec mon matériel. Chaque
              salarié bénéficie d'une séance individuelle de 30 minutes. Je
              prends en général 6 patients par demi-journée d'intervention.
            </Typography>
            <Typography
              sx={{ fontSize: ".88rem", color: C.textMuted, lineHeight: 1.8 }}
            >
              Le planning est établi à l'avance. Vos collaborateurs s'inscrivent
              sur les créneaux qui leur conviennent. Aucun équipement spécifique
              n'est requis de votre côté.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {[
              {
                num: "01",
                title: "Prise de contact",
                desc: "On échange par téléphone ou email pour définir vos besoins, la fréquence et la date de la première intervention.",
              },
              {
                num: "02",
                title: "Planification",
                desc: "Je transmets un planning de créneaux. Vos salariés s'inscrivent librement. Pas de liste d'attente imposée.",
              },
              {
                num: "03",
                title: "Intervention sur site",
                desc: "Je me déplace avec tout le matériel nécessaire. Chaque séance dure 30 minutes, en toute discrétion.",
              },
              {
                num: "04",
                title: "Suivi régulier",
                desc: "Possibilité de mettre en place un rythme mensuel ou trimestriel selon vos besoins et les retours de vos équipes.",
              },
            ].map((step) => (
              <Box
                key={step.num}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "44px 1fr",
                  gap: "1rem",
                  alignItems: "start",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: F.display,
                    fontSize: "1.6rem",
                    fontWeight: 300,
                    color: C.accent,
                    lineHeight: 1,
                    pt: ".1rem",
                  }}
                >
                  {step.num}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: ".88rem",
                      fontWeight: 600,
                      color: C.text,
                      mb: ".2rem",
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: ".8rem",
                      color: C.textMuted,
                      lineHeight: 1.65,
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Tarif ─────────────────────────────── */}
      <Box
        component="section"
        sx={{ background: C.primary, py: { xs: "3rem", md: "5rem" } }}
      >
        <Box
          sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
        >
          <Typography
            sx={{
              fontSize: ".65rem",
              fontWeight: 600,
              letterSpacing: "2.5px",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.5)",
              mb: ".75rem",
            }}
          >
            Tarification
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(1.9rem, 3.5vw, 2.8rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".5rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.65)" },
            }}
          >
            Plus vous êtes nombreux,
            <br />
            <em>moins c'est cher.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.6)",
              maxWidth: "480px",
              lineHeight: 1.75,
              mb: "2.5rem",
            }}
          >
            Séances réalisées directement dans vos locaux, matériel inclus.
            Facturation à l'issue de chaque intervention. Aucun abonnement.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: "1rem",
              mb: "2rem",
            }}
          >
            {[
              { label: "1 à 3 salariés / jour", price: "65", highlight: false },
              { label: "4 à 6 salariés / jour", price: "55", highlight: false },
              { label: "7 salariés et + / jour", price: "45", highlight: true },
              {
                label: "Familles des employés",
                price: "44",
                note: "−20 % sur le tarif cabinet",
                highlight: false,
              },
            ].map((tier) => (
              <Box
                key={tier.label}
                sx={{
                  background: tier.highlight
                    ? "rgba(255,255,255,.18)"
                    : "rgba(255,255,255,.08)",
                  border: tier.highlight
                    ? "1px solid rgba(255,255,255,.4)"
                    : "1px solid rgba(255,255,255,.12)",
                  borderRadius: R.lg,
                  padding: "1.5rem",
                  position: "relative",
                }}
              >
                {tier.highlight && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: C.accent,
                      color: "#fff",
                      fontSize: ".6rem",
                      fontWeight: 700,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      px: ".75rem",
                      py: ".2rem",
                      borderRadius: "50px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Meilleur tarif
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: ".78rem",
                    color: "rgba(255,255,255,.55)",
                    mb: "1rem",
                    lineHeight: 1.4,
                  }}
                >
                  {tier.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: F.display,
                    fontSize: "2.8rem",
                    fontWeight: 300,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  {tier.price}
                  <Box
                    component="sup"
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: 400,
                      verticalAlign: "super",
                    }}
                  >
                    €
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".68rem",
                    color: "rgba(255,255,255,.35)",
                    mt: ".3rem",
                    letterSpacing: ".5px",
                  }}
                >
                  {tier.note || "par personne"}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
            {[
              "Déplacement inclus",
              "Matériel apporté sur place",
              "Facture remise à l'entreprise",
              "Sans abonnement",
            ].map((item) => (
              <Typography
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".5rem",
                  fontSize: ".82rem",
                  color: "rgba(255,255,255,.6)",
                  "&::before": {
                    content: '"✓"',
                    fontWeight: 700,
                    color: "rgba(255,255,255,.45)",
                    flexShrink: 0,
                  },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Partenaires existants ─────────────── */}
      <Box
        component="section"
        sx={{ py: { xs: "3rem", md: "5rem" }, background: C.bgAlt }}
      >
        <Box
          sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
        >
          <Box component="span" sx={SX.sectionLabel}>
            Ils nous font confiance
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "2rem" }}
          >
            Quelques <em>partenaires.</em>
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            {PARTENAIRES.map((p) => (
              <Box
                key={p.name}
                sx={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: R.lg,
                  px: "1.5rem",
                  py: "1.25rem",
                  minWidth: "200px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".2rem",
                  }}
                >
                  {p.name}
                </Typography>
                <Typography
                  sx={{ fontSize: ".75rem", color: C.accent, mb: ".1rem" }}
                >
                  {p.type}
                </Typography>
                <Typography sx={{ fontSize: ".75rem", color: C.textMuted }}>
                  {p.ville}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Formulaire de contact ─────────────── */}
      <Box
        component="section"
        sx={{ background: C.dark, py: { xs: "3.5rem", md: "6rem" } }}
        id="contact"
      >
        <Box
          sx={{
            maxWidth: "720px",
            mx: "auto",
            px: { xs: "1rem", md: "2.5rem" },
          }}
        >
          <Box component="span" sx={SX.sectionLabelWhite}>
            Contact partenariat
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Intéressé ? <em>Parlons-en.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".88rem",
              color: "rgba(255,255,255,.5)",
              mb: "2.5rem",
              lineHeight: 1.7,
            }}
          >
            Remplissez le formulaire ci-dessous ou appelez directement au{" "}
            <strong style={{ color: "rgba(255,255,255,.8)" }}>
              06 95 78 89 83
            </strong>
            . Je vous réponds sous 24h.
          </Typography>

          {sent ? (
            <Box
              sx={{
                background: "rgba(255,255,255,.08)",
                border: "1px solid rgba(255,255,255,.2)",
                borderRadius: R.lg,
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: "1.5rem", mb: ".5rem" }}>
                📬
              </Typography>
              <Typography
                sx={{
                  fontSize: ".95rem",
                  fontWeight: 600,
                  color: "#fff",
                  mb: ".5rem",
                }}
              >
                Plus qu'un clic !
              </Typography>
              <Typography
                sx={{
                  fontSize: ".85rem",
                  color: "rgba(255,255,255,.55)",
                  mb: "1rem",
                  lineHeight: 1.7,
                }}
              >
                Votre client mail vient de s'ouvrir avec le message pré-rempli.
                Il ne reste plus qu'à cliquer sur{" "}
                <strong style={{ color: "rgba(255,255,255,.8)" }}>
                  Envoyer
                </strong>{" "}
                depuis votre messagerie.
              </Typography>
              <Typography
                sx={{
                  fontSize: ".82rem",
                  color: "rgba(255,255,255,.4)",
                  lineHeight: 1.7,
                }}
              >
                Client mail non configuré ? Écrivez directement à{" "}
                <Box
                  component="a"
                  href="mailto:nicolaieffmaxime@gmail.com"
                  sx={{
                    color: "rgba(255,255,255,.65)",
                    textDecoration: "underline",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  nicolaieffmaxime@gmail.com
                </Box>
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: "1rem",
                }}
              >
                <Box
                  component="input"
                  name="nom"
                  placeholder="Votre nom *"
                  required
                  value={form.nom}
                  onChange={handleChange}
                  sx={inputSx}
                />
                <Box
                  component="input"
                  name="entreprise"
                  placeholder="Entreprise / établissement *"
                  required
                  value={form.entreprise}
                  onChange={handleChange}
                  sx={inputSx}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: "1rem",
                }}
              >
                <Box
                  component="input"
                  type="email"
                  name="email"
                  placeholder="Email *"
                  required
                  value={form.email}
                  onChange={handleChange}
                  sx={inputSx}
                />
                <Box
                  component="input"
                  type="tel"
                  name="telephone"
                  placeholder="Téléphone"
                  value={form.telephone}
                  onChange={handleChange}
                  sx={inputSx}
                />
              </Box>
              <Box
                component="textarea"
                name="message"
                placeholder="Votre message  nombre de salariés, fréquence souhaitée, disponibilités…"
                rows={5}
                value={form.message}
                onChange={handleChange}
                sx={{ ...inputSx, resize: "vertical" }}
              />
              <Box
                component="button"
                type="submit"
                sx={{
                  alignSelf: "flex-start",
                  background: C.accent,
                  color: "#fff",
                  border: "none",
                  borderRadius: "50px",
                  px: "2rem",
                  py: ".75rem",
                  fontSize: ".88rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: `all ${trans}`,
                  fontFamily: "inherit",
                  "&:hover": {
                    background: "#b05540",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Envoyer le message
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Partenaires;
